import { useRef, useState } from 'react'

import { MultiSelect, Select, TextInput } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DocumentTemplateSelect from 'src/components/Combobox/DocumentTemplateSelect/DocumentTemplateSelect'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { ADD_DOCUMENT_TEMPLATE } from 'src/graphql/documentTemplatePackages.graphql'
import { CONNECT_DOCUMENT_TEMPLATE } from 'src/graphql/documentTemplatePackages.graphql'
import { UPDATE_DOCUMENT_TEMPLATE } from 'src/graphql/documentTemplates.graphql'
import {
  DocumentCategory,
  DocumentCategoryLabel,
  DocumentSigner,
  DocumentUserStatus,
} from 'src/graphql/types/documents'
import { formatSnakeValue } from 'src/lib/formatters'

const signerOptions = [
  { value: 'APPLICANT', label: 'Applicant' },
  { value: 'RECRUITER', label: 'Recruiter' },
]

const defaultDocument = {
  documentTemplateId: '',
  signers: [DocumentSigner.APPLICANT],
  documentName: '',
  signingToolRefId: '',
  documentCategory: DocumentCategory.OTHER,
  userStatus: DocumentUserStatus.PENDING_REVIEW,
}

const AddDocumentTemplateRow = ({ pkg, refetch }) => {
  const [newDocument, setNewDocument] = useState(defaultDocument)

  const templateJustSelected = useRef(false)

  const canAdd =
    newDocument.signers.length > 0 &&
    newDocument.documentName &&
    newDocument.signingToolRefId &&
    newDocument.documentCategory

  const [addDocument, { loading: adding }] = useMutation(
    ADD_DOCUMENT_TEMPLATE,
    {
      onCompleted: () => {
        setNewDocument(defaultDocument)
        refetch()
      },
      onError: (error) => {
        console.log('error', error.message)
        toast('Unable to add document', 'error')
      },
    }
  )

  const [connectDocument, { loading: connecting }] = useMutation(
    CONNECT_DOCUMENT_TEMPLATE,
    {
      onCompleted: () => {
        refetch()
      },
      onError: () => {
        toast('Unable to connect document', 'error')
      },
    }
  )

  const [updateDocumentTemplate, { loading: updating }] = useMutation(
    UPDATE_DOCUMENT_TEMPLATE,
    {
      onCompleted: () => {
        setNewDocument(defaultDocument)
        refetch()
      },
      onError: () => {
        toast('Unable to update document', 'error')
      },
    }
  )

  const handleAddDocument = async () => {
    if (newDocument.documentTemplateId === '') {
      await addDocument({
        variables: {
          input: {
            signers: newDocument.signers,
            documentName: newDocument.documentName,
            signingToolRefId: newDocument.signingToolRefId,
            documentCategory: newDocument.documentCategory,
            userStatus: newDocument.userStatus,
            status: 'ACTIVE',
            signingTool: 'ANVIL',
            documentTemplatePackageIds: [pkg.documentTemplatePackageId],
          },
        },
      })
    } else {
      await connectDocument({
        variables: {
          documentTemplatePackageId: pkg.documentTemplatePackageId,
          documentTemplateId: newDocument.documentTemplateId,
        },
      })
      await updateDocumentTemplate({
        variables: {
          documentTemplateId: newDocument.documentTemplateId,
          input: {
            userStatus: newDocument.userStatus,
            signers: newDocument.signers,
            documentName: newDocument.documentName,
            documentCategory: newDocument.documentCategory,
          },
        },
      })
    }
  }

  return (
    <div className="grid grid-cols-11 gap-4 px-2">
      <DocumentTemplateSelect
        value={newDocument.documentName}
        onChange={(documentName) => {
          // Skip setting the document name if the template was just selected
          if (!templateJustSelected.current) {
            setNewDocument({
              ...newDocument,
              documentName,
            })
          }
          templateJustSelected.current = false
        }}
        onSelectTemplate={(template) => {
          templateJustSelected.current = true
          setNewDocument({
            documentTemplateId: template.documentTemplateId,
            documentName: template.documentName,
            signingToolRefId: template.signingToolRefId,
            signers: template.signers,
            documentCategory: template.documentCategory,
            userStatus: template.userStatus,
          })
        }}
      />
      <TextInput
        className="col-span-2"
        variant="unstyled"
        size="xs"
        placeholder="Document ID"
        withAsterisk
        value={newDocument.signingToolRefId}
        onChange={(e) =>
          setNewDocument({
            ...newDocument,
            signingToolRefId: e.target.value,
          })
        }
      />
      <Select
        className="col-span-2"
        variant="unstyled"
        size="xs"
        searchable
        data={Object.values(DocumentCategory).map((value) => ({
          value,
          label: DocumentCategoryLabel[value],
        }))}
        placeholder="Category"
        value={newDocument.documentCategory}
        onChange={(value) =>
          setNewDocument({ ...newDocument, documentCategory: value })
        }
      />
      <MultiSelect
        className="col-span-2"
        variant="unstyled"
        searchable
        size="xs"
        placeholder="Select signers"
        classNames={{
          pill: 'rounded-md bg-doubleNickel-gray-100',
          pillsList:
            'flex flex-row flex-nowrap gap-2 max-w-[300px] overflow-auto',
        }}
        data={signerOptions}
        value={newDocument.signers}
        onChange={(value) => setNewDocument({ ...newDocument, signers: value })}
      />
      <Select
        className="col-span-2"
        variant="unstyled"
        size="xs"
        placeholder="Status"
        data={Object.values(DocumentUserStatus).map((value) => ({
          value,
          label: formatSnakeValue(value),
        }))}
        value={newDocument.userStatus}
        onChange={(value) =>
          setNewDocument({ ...newDocument, userStatus: value })
        }
      />
      <DNButton
        className="ml-auto"
        variant="secondary"
        disabled={!canAdd}
        loading={adding || connecting || updating}
        onClick={handleAddDocument}
      >
        Add
      </DNButton>
    </div>
  )
}

export default AddDocumentTemplateRow
